import AvatarWithStatus from './AvatarWithStatus';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import Sheet from '@mui/joy/Sheet';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';

import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';

import { APIGetMessageViews } from '../../api';
import { MOTION, avatarGradient, bubbleTime, ellipsis, initialsOf } from './chatTheme';

/**
 * Полоска «кто прочитал» под лентой.
 *
 * Показывает прочтения ПОСЛЕДНЕГО собственного сообщения и появляется ровно
 * тогда, когда его кто-то открыл. До этого её нет совсем: пустая строка
 * «прочитали 0» занимает место и ничего не сообщает.
 *
 * Почему именно последнее своё сообщение. Галочки на каждом пузыре отвечают
 * на вопрос «дошло ли», а человеку обычно нужен другой: «меня вообще
 * прочитали?». Ответ на него — состояние последней реплики, и держать его
 * стоит там, куда смотрят после отправки, — над полем ввода.
 *
 * Список прочитавших открывается по наведению и по нажатию: на телефоне
 * наведения нет, а знать, кто именно прочитал, там нужно ничуть не меньше.
 */
export default function ReadReceiptsBar({ chatId, token, messageId, refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const load = useCallback(async () => {
    if (!chatId || !messageId) { setData(null); return; }
    const res = await APIGetMessageViews(chatId, messageId, token);
    if (!aliveRef.current) return;
    if (res?.status === 'ok') setData(res);
  }, [chatId, messageId, token]);

  // Событий о прочтении приходит пачка — по одному на каждого, кто открыл
  // чат. Небольшая задержка склеивает их в один запрос, иначе после
  // планёрки полоска дёргается и шлёт десяток запросов подряд.
  useEffect(() => {
    clearTimeout(timerRef.current);
    if (!chatId || !messageId) { setData(null); return undefined; }
    timerRef.current = setTimeout(load, refreshKey ? 400 : 0);
    return () => clearTimeout(timerRef.current);
  }, [load, refreshKey, chatId, messageId]);

  // Своё сообщение сменилось — старые прочтения показывать нельзя ни секунды:
  // «прочитали» под только что отправленным было бы прямым враньём.
  useEffect(() => { setData(null); setOpen(false); }, [messageId]);

  const views = useMemo(() => data?.views || [], [data]);
  const total = Number(data?.recipients_total || 0);
  const count = views.length;

  const label = useMemo(() => {
    if (count === 0) return '';
    if (total > 1 && count < total) return `Прочитали ${count} из ${total}`;
    if (count === 1) return `Прочитал${total === 1 ? '' : 'и'}: ${firstName(views[0]?.name)}`;
    if (count === total) return `Прочитали все (${count})`;
    return `Прочитали ${count}`;
  }, [count, total, views]);

  return (
    <AnimatePresence initial={false}>
      {count > 0 && (
        <Box
          component={motion.div}
          key="receipts"
          initial={{ opacity: 0, height: 0, y: 6 }}
          animate={{ opacity: 1, height: 'auto', y: 0 }}
          exit={{ opacity: 0, height: 0, y: 6 }}
          transition={{ duration: MOTION.base, ease: MOTION.ease }}
          sx={{ px: { xs: 1.5, md: 3 }, overflow: 'hidden', flexShrink: 0 }}
        >
          <Tooltip
            placement="top-end"
            variant="outlined"
            open={open}
            onClose={() => setOpen(false)}
            sx={{ p: 0, borderRadius: '16px', boxShadow: 'lg', zIndex: 10030, maxWidth: 280 }}
            title={<ViewersList views={views} total={total} />}
          >
            <Stack
              component={motion.div}
              whileTap={{ scale: 0.98 }}
              direction="row"
              spacing={0.75}
              onClick={() => setOpen((v) => !v)}
              onMouseEnter={() => setOpen(true)}
              onMouseLeave={() => setOpen(false)}
              sx={{
                alignItems: 'center',
                alignSelf: 'flex-end',
                width: 'fit-content',
                ml: 'auto',
                mb: 0.5,
                px: 1, py: 0.375,
                borderRadius: '999px',
                cursor: 'pointer',
                userSelect: 'none',
                backgroundColor: 'rgba(34,197,94,0.10)',
                transition: 'background-color .16s',
                '&:hover': { backgroundColor: 'rgba(34,197,94,0.18)' },
              }}
            >
              <DoneAllRoundedIcon sx={{ fontSize: 15, color: '#16a34a', flexShrink: 0 }} />

              {/* Стопка аватаров: лица читаются быстрее имён, а имена всё
                  равно рядом — в подписи и в списке. */}
              <Stack direction="row" sx={{ flexShrink: 0, pl: 0.25 }}>
                {views.slice(0, 3).map((v, i) => (
                  <MiniAvatar key={`${v.kind}:${v.id}`} name={v.name} person={v} offset={i} />
                ))}
              </Stack>

              <Typography
                level="body-xs"
                sx={{
                  ...ellipsis, fontSize: 11, fontWeight: 650,
                  color: '#15803d', maxWidth: 220,
                }}
              >
                {label}
              </Typography>
            </Stack>
          </Tooltip>
        </Box>
      )}
    </AnimatePresence>
  );
}

/** Кружок с инициалами, наезжающий на соседний. */
function MiniAvatar({ name, person, offset = 0 }) {
  return <AvatarWithStatus name={name} person={person} size="sm" sx={{ width: 18, height: 18,
    ml: offset === 0 ? 0 : '-6px', fontSize: 8, border: '1.5px solid var(--joy-palette-background-surface, #fff)', zIndex: 3 - offset }}/>
}

/** Содержимое тултипа: кто и во сколько прочитал. */
function ViewersList({ views, total }) {
  return (
    <Sheet
      component={motion.div}
      initial={{ opacity: 0, y: 6, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: MOTION.fast, ease: MOTION.ease }}
      variant="plain"
      sx={{ borderRadius: '16px', p: 1.25, minWidth: 190, background: 'transparent' }}
    >
      <Typography
        level="body-xs"
        sx={{
          fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em',
          color: 'text.tertiary', fontSize: 9.5, mb: 0.75,
        }}
      >
        Прочитали{total > 0 ? ` · ${views.length} из ${total}` : ''}
      </Typography>

      <Stack spacing={0.75} sx={{ maxHeight: 210, overflowY: 'auto' }}>
        {views.map((v) => (
          <Stack key={`${v.kind}:${v.id}`} direction="row" spacing={1}
                 sx={{ alignItems: 'center', minWidth: 0 }}>
            <MiniAvatar name={v.name} person={v} />
            <Typography level="body-xs"
              sx={{ ...ellipsis, flex: 1, fontSize: 11.5, fontWeight: 600 }}>
              {v.name}
            </Typography>
            {v.read_at && (
              <Typography level="body-xs"
                sx={{ flexShrink: 0, fontSize: 10.5, color: 'text.tertiary' }}>
                {bubbleTime(v.read_at)}
              </Typography>
            )}
          </Stack>
        ))}
      </Stack>
    </Sheet>
  );
}

function firstName(full) {
  const parts = String(full || '').trim().split(/\s+/).filter(Boolean);
  // «Иванов Пётр» — в подписи оставляем имя, а не фамилию: так короче и
  // так обращаются друг к другу внутри компании.
  return parts.length > 1 ? parts[1] : (parts[0] || '');
}
